import type { CSSProperties } from "react";
import { NavigationLink, NavLinkType } from "@makula/ui";
import { WorkspaceSwitcher } from "../shared/WorkspaceSwitcher";
import type { RootView } from "../shared/productTypes";

export type CmmsActivePage =
  | "Assets"
  | "Parts"
  | "Work Orders"
  | "My Work Orders"
  | "Procedures"
  | "Scheduler"
  | "PM Events"
  | "Insights"
  | "Archive"
  | "Companies"
  | "AI Notetaker"
  | "AI Chats";

interface CmmsSidebarProps {
  activePage: CmmsActivePage;
  onNavigate: (page: CmmsActivePage) => void;
  onNavigateRoot: (view: RootView) => void;
}

const Divider = () => (
  <div
    style={{
      height: 1,
      background: "var(--color-border-primary)",
      width: "100%",
      flexShrink: 0,
    }}
  />
);

function NavGroup({
  items,
  activePage,
  onNavigate,
}: {
  items: CmmsActivePage[];
  activePage: CmmsActivePage;
  onNavigate: (page: CmmsActivePage) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-3xs)", width: "100%", flexShrink: 0 }}>
      {items.map((type) => (
        <NavigationLink
          key={type}
          type={type as NavLinkType}
          active={activePage === type}
          onClick={() => onNavigate(type)}
        />
      ))}
    </div>
  );
}

export function CmmsSidebar({ activePage, onNavigate, onNavigateRoot }: CmmsSidebarProps) {
  const sidebarStyle: CSSProperties = {
    width: 224,
    height: "100vh",
    background: "var(--color-bg-accent)",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    position: "sticky",
    top: 0,
    overflow: "hidden",
  };

  const headerStyle: CSSProperties = {
    padding: "var(--spacing-m) var(--spacing-l)",
    borderBottom: "1px solid var(--color-border-primary)",
    borderRight: "1px solid var(--color-border-primary)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--spacing-l)",
    flexShrink: 0,
  };

  const middleStyle: CSSProperties = {
    flex: "1 0 0",
    padding: "var(--spacing-l)",
    borderRight: "1px solid var(--color-border-primary)",
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: "var(--spacing-xs)",
    minHeight: 0,
  };

  return (
    <aside style={sidebarStyle}>
      <div style={headerStyle}>
        <WorkspaceSwitcher
          activeProductId="cmms"
          onSelectProduct={onNavigateRoot}
          onOpenDashboard={() => onNavigateRoot("home")}
        />
      </div>

      <div style={middleStyle}>
        <NavGroup items={["Assets", "Parts"]} activePage={activePage} onNavigate={onNavigate} />
        <Divider />

        <NavGroup
          items={["Work Orders", "My Work Orders", "Procedures", "Scheduler", "PM Events", "Insights", "Archive"]}
          activePage={activePage}
          onNavigate={onNavigate}
        />
        <Divider />

        <NavGroup items={["Companies"]} activePage={activePage} onNavigate={onNavigate} />
        <Divider />

        <NavGroup items={["AI Notetaker", "AI Chats"]} activePage={activePage} onNavigate={onNavigate} />
      </div>
    </aside>
  );
}
